import { addMonths, format, parseISO, subMonths } from 'date-fns';
import type { ProductionGroup } from '@/types';
import type { PigTallyDay } from '@/types/SectionLiveKpi';

export type ProductionGroupTimelineRange = {
    from: string;
    to: string;
};

export const PRODUCTION_GROUP_TIMELINE_MONTHS_BACK = 3;
export const PRODUCTION_GROUP_TIMELINE_MONTHS_FORWARD = 4;

// Centered on `center` (the form's own start date once one is picked, else today) rather than a
// fixed "today - N months" window, so the timeline follows whichever Production Group is actually
// being looked at instead of always showing only what's recent.
export function getProductionGroupTimelineRange(center = new Date()): ProductionGroupTimelineRange {
    return {
        from: format(subMonths(center, PRODUCTION_GROUP_TIMELINE_MONTHS_BACK), 'yyyy-MM-dd'),
        to: format(addMonths(center, PRODUCTION_GROUP_TIMELINE_MONTHS_FORWARD), 'yyyy-MM-dd'),
    };
}

export function findSuggestedProductionGroupStart(tally: PigTallyDay[]): string | null {
    const orderedTally = [...tally].sort((left, right) => left.date.localeCompare(right.date));

    for (let index = orderedTally.length - 1; index > 0; index -= 1) {
        const previousDay = orderedTally[index - 1];
        const day = orderedTally[index];
        if (previousDay.pigs === 0 && day.pigs > 0) return day.date;
    }

    return null;
}

export function productionGroupIntersectsTimeline(productionGroup: ProductionGroup, range: ProductionGroupTimelineRange): boolean {
    const start = toDateOnly(productionGroup.start);
    const end = productionGroup.end ? toDateOnly(productionGroup.end) : null;
    return start <= range.to && (end === null || end >= range.from);
}

export type ProductionGroupOverlapQuery = {
    start: string;
    end: string | null;
    productionGroups: ProductionGroup[];
    today: string;
    // Excluded from the search — the Production Group currently being edited, so it doesn't "overlap" itself.
    ignoredProductionGroupId: number | null;
};

export function findProductionGroupOverlap({ start, end, productionGroups, today, ignoredProductionGroupId }: ProductionGroupOverlapQuery): ProductionGroup | null {
    const candidateEnd = end ?? today;
    return (
        productionGroups.find((productionGroup) => {
            if (productionGroup.id === ignoredProductionGroupId) return false;

            const existingStart = toDateOnly(productionGroup.start);
            const existingEnd = productionGroup.end ? toDateOnly(productionGroup.end) : today;
            return existingStart <= candidateEnd && start <= existingEnd;
        }) ?? null
    );
}

export function pigTallyAtDate(tally: PigTallyDay[], date: string): number | null {
    return tally.find((day) => day.date === date)?.pigs ?? null;
}

// A group's own Start/End must span at least 2 calendar days - Start == End would give that one
// day's daily-granularity telemetry (pig-tally/water/weight) to a group that can't be cleanly told
// apart from whatever preceded or follows it on the same day. Shared by every surface that creates
// or edits a Production Group's dates, so they all enforce the same rule.
export function isProductionGroupRangeTooShort(start: string, end: string | null): boolean {
    return end !== null && Date.parse(end) <= Date.parse(start);
}

function toDateOnly(value: string): string {
    return format(parseISO(value), 'yyyy-MM-dd');
}
